import {
  VA_FIELD_LIMITS,
  GUARDIANSHIP_FIELD_LIMITS,
  PROBATE_FIELD_LIMITS,
  TRUSTEE_FIELD_LIMITS,
} from "../../../../globals/utils/bondFieldLimits";
import { calculatePremium } from "../../../../features/premium/domain/calculatePremium";
import { applyPremiumRecalcToPayload } from "../../../../features/bonds/shared/bondWizardStepHelpers";
import { buildCommonHistoricPayload } from "./historicPayloadBuilders";
import { normalizeGuardiansForPayload } from "../../../../features/bonds/shared/guardianCitizenshipHelpers";
import {
  buildHistoricBillingFields,
  buildHistoricDeclineFields,
  buildHistoricDocumentFields,
  buildHistoricFiduciaryFields,
  buildHistoricPrincipalDisplayName,
} from "./historicBondSharedHelpers";

export { toHistoricEffectiveDateIso } from "./historicPayloadBuilders";

const STANDARD_GUARDIAN_ERROR_FIELD_LABELS = {
  name: "Name",
  address: "Address",
  zip_code: "ZIP Code",
  city: "City",
  state: "State",
};

const GUARDIANSHIP_GUARDIAN_ERROR_FIELD_LABELS = {
  guardian_name: "Name",
  guardian_street_address_1: "Address",
  guardian_zip: "ZIP Code",
  guardian_city: "City",
  guardian_state: "State",
};

const STANDARD_GUARDIAN_FIELDS = ["name", "address", "zip_code", "city", "state"];
const GUARDIANSHIP_GUARDIAN_FIELDS = [
  "guardian_name",
  "guardian_street_address_1",
  "guardian_zip",
  "guardian_city",
  "guardian_state",
];

export function buildHistoricRequiredFieldsPerStep(basicInformation, attorneyFields = []) {
  return {
    basicInformation,
    generalInformation: [],
    guardianDetails: [],
    attorneyInformation: attorneyFields,
    obligeeInformation: ["obligeeId"],
    requestorInformation: ["firstNameReq", "lastNameReq", "email", "phoneNumber"],
    documentUpload: [],
    billingInformation: ["billing_entity"],
    otherDetails: ["effectiveDate", "bond_number"],
  };
}

export function createHistoricBondConfig(kind, overrides = {}) {
  const isGuardianship = kind === "guardianship";
  const isTrustee = kind === "trustee";

  return {
    guardianCountField: "PrincipalNumber",
    guardianFieldNames: isGuardianship ? GUARDIANSHIP_GUARDIAN_FIELDS : STANDARD_GUARDIAN_FIELDS,
    guardianErrorFieldLabels: isGuardianship
      ? GUARDIANSHIP_GUARDIAN_ERROR_FIELD_LABELS
      : STANDARD_GUARDIAN_ERROR_FIELD_LABELS,
    getSupportName: isTrustee
      ? (globalState) => globalState?.basicInformation?.trustName || ""
      : (globalState) =>
          [globalState?.basicInformation?.firstName, globalState?.basicInformation?.lastName]
            .filter(Boolean)
            .join(" "),
    ...overrides,
  };
}

export const VA_HISTORIC_CREATE_CONFIG = createHistoricBondConfig("va", {
  apiEndpoint: "/create_va_bond_historic/",
  supportBondType: "VA (Historic)",
  sectionLimitsMap: {
    basicInformation: VA_FIELD_LIMITS.basicInformation,
    generalInformation: VA_FIELD_LIMITS.generalInformation,
    guardianDetails: VA_FIELD_LIMITS.principalInformation,
    requestorInformation: VA_FIELD_LIMITS.requestorInformation,
    billingInformation: VA_FIELD_LIMITS.billingInformation,
  },
  getRequiredFieldsPerStep: (globalState) =>
    buildHistoricRequiredFieldsPerStep([
      "firstName",
      "lastName",
      "dateOfBirth",
      "bondAmount",
      "PrincipalNumber",
      ...(globalState.basicInformation?.hasCourtCaseNumber === "Yes"
        ? ["courtCaseNumber"]
        : []),
    ]),
  buildPayload: (globalState, context) => ({
    ...buildCommonHistoricPayload(globalState, context),
    bond_type: 6,
    bond_sub_category: "None",
    name: buildHistoricPrincipalDisplayName(globalState.basicInformation),
    vabond: {
      beneficiary_legal_name: [
        globalState.basicInformation?.firstName,
        globalState.basicInformation?.middleName,
        globalState.basicInformation?.lastName,
        globalState.basicInformation?.suffix,
      ]
        .filter(Boolean)
        .join(" "),
      beneficiary_date_of_birth: globalState.basicInformation?.dateOfBirth,
      number_of_principals: globalState.basicInformation?.PrincipalNumber,
      principals_all_us_canadian_uk_citizens: true,
      ...buildHistoricFiduciaryFields(globalState, "va"),
      beneficiary_first_name: globalState.basicInformation?.firstName,
      beneficiary_middle_name: globalState.basicInformation?.middleName,
      beneficiary_last_name: globalState.basicInformation?.lastName,
      beneficiary_suffix: globalState.basicInformation?.suffix,
      beneficiary_alias: globalState.basicInformation?.aliases,
      ...buildHistoricDocumentFields(globalState.documentUpload),
      ...buildHistoricBillingFields(globalState.billingInformation),
      ...buildHistoricDeclineFields(globalState.generalInformation, { creditReport: true }),
    },
    principals: globalState.guardianDetails?.guardians || [],
  }),
});

export const GUARDIANSHIP_HISTORIC_CREATE_CONFIG = createHistoricBondConfig("guardianship", {
  apiEndpoint: "/create_guardianship_bond_historic/",
  supportBondType: "Guardianship (Historic)",
  sectionLimitsMap: {
    basicInformation: GUARDIANSHIP_FIELD_LIMITS.basicInformation,
    generalInformation: GUARDIANSHIP_FIELD_LIMITS.generalInformation,
    guardianDetails: GUARDIANSHIP_FIELD_LIMITS.principalInformation,
    requestorInformation: GUARDIANSHIP_FIELD_LIMITS.requestorInformation,
    billingInformation: GUARDIANSHIP_FIELD_LIMITS.billingInformation,
  },
  getRequiredFieldsPerStep: () =>
    buildHistoricRequiredFieldsPerStep(
      [
        "bondSubCategory",
        "county",
        "firstName",
        "lastName",
        "dateOfDeath",
        "bondAmount",
        "GuardianNumber",
      ],
      ["lawFirm", "lawFirmAddress", "attorneyOfRecord"]
    ),
  buildPayload: (globalState, context) => ({
    ...buildCommonHistoricPayload(globalState, context),
    bond_type: 3,
    bond_sub_category: globalState.basicInformation?.bondSubCategory,
    name: buildHistoricPrincipalDisplayName(globalState.basicInformation),
    guardianshipbond: {
      ward_legal_name: [
        globalState.basicInformation?.firstName,
        globalState.basicInformation?.middleName,
        globalState.basicInformation?.lastName,
        globalState.basicInformation?.suffix,
      ]
        .filter(Boolean)
        .join(" "),
      ward_date_of_birth: globalState.basicInformation?.dateOfDeath,
      number_of_guardians: globalState.basicInformation?.GuardianNumber,
      guardians_all_us_canadian_uk_citizens: true,
      ...buildHistoricFiduciaryFields(globalState, "guardianship"),
      ward_first_name: globalState.basicInformation?.firstName,
      ward_middle_name: globalState.basicInformation?.middleName,
      ward_last_name: globalState.basicInformation?.lastName,
      ward_suffix: globalState.basicInformation?.suffix,
      ward_alias: globalState.basicInformation?.aliases,
      ...buildHistoricDocumentFields(globalState.documentUpload),
      ...buildHistoricBillingFields(globalState.billingInformation),
      ...buildHistoricDeclineFields(globalState.generalInformation),
    },
    guardians: normalizeGuardiansForPayload(
      globalState.guardianDetails?.guardians || []
    ),
  }),
  preparePayload: (payload, globalState) => {
    applyPremiumRecalcToPayload(payload, calculatePremium, [
      globalState.basicInformation?.dateOfDeath,
      globalState.basicInformation?.bondSubCategory,
    ]);
    return payload;
  },
});

export const PROBATE_HISTORIC_CREATE_CONFIG = createHistoricBondConfig("probate", {
  apiEndpoint: "/create_probate_bond_historic/",
  supportBondType: "Probate (Historic)",
  sectionLimitsMap: {
    basicInformation: PROBATE_FIELD_LIMITS.basicInformation,
    generalInformation: PROBATE_FIELD_LIMITS.generalInformation,
    guardianDetails: PROBATE_FIELD_LIMITS.principalInformation,
    requestorInformation: PROBATE_FIELD_LIMITS.requestorInformation,
    billingInformation: PROBATE_FIELD_LIMITS.billingInformation,
  },
  getRequiredFieldsPerStep: () =>
    buildHistoricRequiredFieldsPerStep(
      [
        "bondSubCategory",
        "county",
        "firstName",
        "lastName",
        "dateOfDeath",
        "bondAmount",
        "PrincipalNumber",
      ],
      ["lawFirm", "lawFirmAddress", "attorneyOfRecord"]
    ),
  buildPayload: (globalState, context) => ({
    ...buildCommonHistoricPayload(globalState, context),
    bond_type: 1,
    bond_sub_category: globalState.basicInformation?.bondSubCategory,
    name: buildHistoricPrincipalDisplayName(globalState.basicInformation),
    probatebond: {
      deceased_legal_name: [
        globalState.basicInformation?.firstName,
        globalState.basicInformation?.middleName,
        globalState.basicInformation?.lastName,
        globalState.basicInformation?.suffix,
      ]
        .filter(Boolean)
        .join(" "),
      date_of_death: globalState.basicInformation?.dateOfDeath,
      number_of_personal_representative:
        globalState.basicInformation?.PrincipalNumber,
      us_citizen: true,
      estate_include_ongoing_business:
        globalState.generalInformation?.ongoingBusiness === "Yes",
      estate_include_ongoing_business_explanation:
        globalState.generalInformation?.explanations?.ongoingBusiness,
      personal_rep_indebted:
        globalState.generalInformation?.indebtedGuardians === "Yes",
      execute_last_will_testament:
        globalState.generalInformation?.didLastWillAndTestament === "Yes",
      copy_of_last_will_and_testament:
        globalState.documentUpload?.lastWillFileName,
      copy_of_last_will_and_testament_link:
        globalState.documentUpload?.lastWillLink,
      replace_prior_fiduciary:
        globalState.generalInformation?.replacingFiduciary === "Yes",
      replace_prior_fiduciary_explanation:
        globalState.generalInformation?.explanations?.replacingFiduciary,
      replace_prior_bond:
        globalState.generalInformation?.replacingPriorBond === "Yes",
      demand_other_than_court:
        globalState.generalInformation?.demandedByInterestedParty === "Yes",
      demand_other_than_court_explanation:
        globalState.generalInformation?.explanations?.demandedByInterestedParty,
      estimated_estate_assets:
        globalState.generalInformation?.estAssets === ""
          ? null
          : globalState.generalInformation?.estAssets,
      deceased_legal_first_name: globalState.basicInformation?.firstName,
      deceased_legal_middle_name: globalState.basicInformation?.middleName,
      deceased_legal_last_name: globalState.basicInformation?.lastName,
      deceased_legal_suffix: globalState.basicInformation?.suffix,
      alias_deceased_legal_name: globalState.basicInformation?.aliases,
      ever_bankrupt_insolvent:
        globalState.generalInformation?.bankruptOrInsolvent === "Yes",
      outstanding_lawsuits:
        globalState.generalInformation?.outstandingLawsuit === "Yes",
      any_disputes: globalState.generalInformation?.anyDisputes === "Yes",
      personal_rep_indebted_explanation:
        globalState.generalInformation?.explanations?.indebtedGuardians,
      declined_by_any_other_surety_explanation:
        globalState.generalInformation?.explanations?.isDeclined,
      outstanding_lawsuits_explanation:
        globalState.generalInformation?.explanations?.outstandingLawsuitExplanation,
      ever_bankrupt_insolvent_explanation:
        globalState.generalInformation?.explanations?.bankruptOrInsolventExplanation,
      any_disputes_explanation:
        globalState.generalInformation?.explanations?.anyDisputes,
      ...buildHistoricDocumentFields(globalState.documentUpload),
      ...buildHistoricBillingFields(globalState.billingInformation),
      credit_report_allowed:
        globalState.generalInformation?.requestForAuthorization === "Yes",
      declined_by_any_other_surety:
        globalState.generalInformation?.isDeclined === "Yes",
      is_judge_stearn: globalState.basicInformation?.judgeStern === "Yes",
      is_wrongful_death:
        globalState.basicInformation?.wrongfulDeathLawsuit === "Yes" ||
        globalState.basicInformation?.wrongfulDeathLawsuit === null,
    },
    personal_rep: globalState.guardianDetails?.guardians || [],
  }),
});

export const TRUSTEE_HISTORIC_CREATE_CONFIG = createHistoricBondConfig("trustee", {
  apiEndpoint: "/create_historic_trustee_bond/",
  supportBondType: "Trustee (Historic)",
  sectionLimitsMap: {
    basicInformation: TRUSTEE_FIELD_LIMITS.basicInformation,
    generalInformation: TRUSTEE_FIELD_LIMITS.generalInformation,
    guardianDetails: TRUSTEE_FIELD_LIMITS.principalInformation,
    requestorInformation: TRUSTEE_FIELD_LIMITS.requestorInformation,
    billingInformation: TRUSTEE_FIELD_LIMITS.billingInformation,
  },
  getRequiredFieldsPerStep: () =>
    buildHistoricRequiredFieldsPerStep([
      "county",
      "legalName",
      "dateOfBirth",
      "bondAmount",
      "PrincipalNumber",
    ]),
  buildPayload: (globalState, context) => ({
    ...buildCommonHistoricPayload(globalState, context),
    bond_type: 4,
    bond_sub_category: globalState.basicInformation?.bondSubCategory,
    name: globalState.basicInformation?.legalName,
    trustee_bond: {
      county: globalState.basicInformation?.county,
      amount: globalState.basicInformation?.bondAmount,
      court_file_no:
        globalState.basicInformation?.hasCourtCaseNumber === "Yes"
          ? globalState.basicInformation?.courtCaseNumber
          : "",
      legal_name: globalState.basicInformation?.legalName,
      trust_name: globalState.basicInformation?.trustName,
      dob: globalState.basicInformation?.dateOfBirth,
      number_of_principals: globalState.basicInformation?.PrincipalNumber,
      all_us_canadian_uk_citizens:
        globalState.generalInformation?.allUSUKCanada === "Yes",
      all_us_canadian_uk_citizens_explanation: "",
      any_business_operated:
        globalState.generalInformation?.ongoingBusiness === "Yes",
      any_business_operated_explanation:
        globalState.generalInformation?.explanations?.ongoingBusiness,
      indebted_state:
        globalState.generalInformation?.indebtedGuardians === "Yes",
      indebted_state_explanation:
        globalState.generalInformation?.explanations?.indebtedGuardians,
      is_replaced_prior:
        globalState.generalInformation?.replacingPriorBond === "Yes",
      is_replaced_prior_explanation:
        globalState.generalInformation?.explanations?.replacingPriorBond,
      is_supplemental_needs:
        globalState.generalInformation?.supplementalNeedsTrust === "Yes",
      ...buildHistoricDocumentFields(globalState.documentUpload),
      ...buildHistoricBillingFields(globalState.billingInformation),
      credit_report_allowed:
        globalState.generalInformation?.requestForAuthorization === "Yes",
    },
    trustees: globalState.guardianDetails?.guardians || [],
  }),
});
